import { Position } from '../constants';

import { Entity, EntityType, EntityData } from './Entity';

// Event types for entity changes
export type EntityEvent = 'entityAdded' | 'entityRemoved' | 'entityUpdated';
export type EntityEventCallback = (entity: Entity) => void;

// Centralized manager for all entities in the game world
export class EntityManager {
  private entities: Map<string, Entity> = new Map();
  private eventListeners: Map<EntityEvent, Set<EntityEventCallback>> =
    new Map();
  private nextEntityId: number = 1;

  constructor() {
    // Initialize event listener maps
    this.eventListeners.set('entityAdded', new Set());
    this.eventListeners.set('entityRemoved', new Set());
    this.eventListeners.set('entityUpdated', new Set());
  }

  // Generate unique entity ID
  generateEntityId(prefix: string = 'entity'): string {
    const id = `${prefix}_${this.nextEntityId}`;
    this.nextEntityId++;
    return id;
  }

  // Add entity to the world
  addEntity(entity: Entity): void {
    if (this.entities.has(entity.id)) {
      throw new Error(`Entity with ID ${entity.id} already exists`);
    }

    this.entities.set(entity.id, entity);
    // Register with collision service if entity has collision behavior
    // TODO: Convert entity to CollisionEntity format when needed
    this.emitEvent('entityAdded', entity);
  }

  // Remove entity from the world
  removeEntity(entityId: string): boolean {
    const entity = this.entities.get(entityId);
    if (!entity) {
      return false;
    }

    // Remove from collision service if registered
    // TODO: Remove collision entity when needed
    this.entities.delete(entityId);
    this.emitEvent('entityRemoved', entity);
    return true;
  }

  // Get entity by ID
  getEntity(entityId: string): Entity | undefined {
    return this.entities.get(entityId);
  }

  // Get all entities at a specific position
  getEntitiesAt(position: Position): Entity[] {
    return Array.from(this.entities.values()).filter(entity =>
      entity.isAt(position)
    );
  }

  // Get all entities of a specific type
  getEntitiesByType(type: EntityType): Entity[] {
    return Array.from(this.entities.values()).filter(
      entity => entity.type === type
    );
  }

  // Get all entities of specific types at a position
  getEntitiesAtByType(position: Position, types: EntityType[]): Entity[] {
    return this.getEntitiesAt(position).filter(entity =>
      types.includes(entity.type)
    );
  }

  // Get all entities
  getAllEntities(): Entity[] {
    return Array.from(this.entities.values());
  }

  // Check if position has any solid entities
  hasSolidEntityAt(position: Position): boolean {
    return this.getEntitiesAt(position).some(entity => entity.isSolid());
  }

  // Update all entities (called each game tick)
  updateAll(deltaTime: number): void {
    // Update all entities
    this.entities.forEach(entity => {
      const oldPosition = { ...entity.position };
      entity.update(deltaTime);

      // Emit update event if entity moved
      if (
        oldPosition.x !== entity.position.x ||
        oldPosition.y !== entity.position.y
      ) {
        this.emitEvent('entityUpdated', entity);
      }
    });

    // Remove entities marked for destruction
    const entitiesToRemove = Array.from(this.entities.values())
      .filter(entity => entity.shouldDestroy())
      .map(entity => entity.id);

    for (const entityId of entitiesToRemove) {
      this.removeEntity(entityId);
    }
  }

  // Clear all entities
  clear(): void {
    const entityIds = Array.from(this.entities.keys());
    for (const entityId of entityIds) {
      this.removeEntity(entityId);
    }
  }

  // Serialize all entities for saving
  serialize(): EntityData[] {
    return Array.from(this.entities.values()).map(entity => entity.serialize());
  }

  // Deserialize entities from save data
  deserialize(_entitiesData: EntityData[]): void {
    this.clear();

    // Entity deserialization will be handled by specific entity classes
    // For now, we'll add this hook for future implementation
    // Entity deserialization not yet implemented
  }

  // Event system
  addEventListener(event: EntityEvent, callback: EntityEventCallback): void {
    this.eventListeners.get(event)?.add(callback);
  }

  removeEventListener(event: EntityEvent, callback: EntityEventCallback): void {
    this.eventListeners.get(event)?.delete(callback);
  }

  private emitEvent(event: EntityEvent, entity: Entity): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(entity);
        } catch {
          // Error in entity event listener
        }
      });
    }
  }

  // Debug/utility methods
  getEntityCount(): number {
    return this.entities.size;
  }

  getEntityCountByType(type: EntityType): number {
    return this.getEntitiesByType(type).length;
  }

  logStats(): void {
    // EntityManager Stats - silent for production
  }
}
